import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import ClientDogs from "@/pages/ClientDogs";
import { PageHeader, StatusBadge, fmtDate, initials } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Dog as DogIcon, Weight, Cake, Target, HeartPulse, Pencil, Trash2, ClipboardList, CalendarDays, Phone, Mail, StickyNote } from "lucide-react";
import { toast } from "sonner";

const DOG_IMAGES = [
  "https://images.unsplash.com/photo-1612940960267-4549a58fb257",
  "https://images.unsplash.com/photo-1761069234314-aa161222c6c2",
];

const LEVELS = [
  { value: "puppy", label: "Puppy" }, { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" }, { value: "advanced", label: "Advanced" },
];

const empty = {
  name: "", client_id: "", location_id: "", assigned_trainer_id: "", breed: "", age: "",
  gender: "", weight: "", vaccination_status: "up_to_date", behaviour_notes: "",
  training_goals: "", current_level: "puppy", progress_status: "on_track",
};

export default function Dogs() {
  const { user } = useAuth();
  if (user?.role === "client") return <ClientDogs />;
  return <AdminDogs />;
}

function AdminDogs() {
  const { user } = useAuth();
  const ref = useRefData();
  const canManage = user?.role !== "client" && user?.role !== "trainer";
  const [dogs, setDogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/dogs");
    setDogs(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (d) => {
    setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, d[k] ?? empty[k]])) });
    setEditing(d); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/dogs/${editing.id}`, form);
      else await api.post("/dogs", form);
      toast.success(editing ? "Dog updated" : "Dog created");
      setOpen(false); await load(); ref.reload();
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/dogs/${d.id}`);
    toast.success("Deleted"); await load(); ref.reload();
    if (detail?.id === d.id) setDetail(null);
  };

  const openDetail = async (d) => {
    const r = await api.get(`/dogs/${d.id}`);
    setDetail(r.data);
  };

  const setF = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div>
      <PageHeader
        title="Dog Profiles"
        subtitle="The central record of every dog in your training journey."
        action={canManage && <Button onClick={openCreate} className="gap-2" data-testid="dogs-add-btn"><Plus size={16} strokeWidth={1.5} /> New Dog</Button>}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {dogs.map((d, i) => (
          <Card
            key={d.id}
            className="border-border overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => openDetail(d)}
            data-testid="dog-card"
          >
            <div className="h-36 relative overflow-hidden">
              <img src={DOG_IMAGES[i % DOG_IMAGES.length]} alt={d.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-3 right-3"><StatusBadge value={d.current_level} /></div>
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-semibold text-lg">{d.name}</div>
                  <div className="text-sm text-muted-foreground">{d.breed || "Unknown breed"}</div>
                </div>
                <StatusBadge value={d.progress_status} />
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Cake size={13} /> {d.age || "—"}</span>
                <span className="flex items-center gap-1"><Weight size={13} /> {d.weight || "—"}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground">
                {d.location_name || "—"} · Trainer: {d.trainer_name || "Unassigned"}
              </div>
              {d.current_program && <div className="mt-1.5 text-xs text-muted-foreground">Program: <span className="text-foreground font-medium">{d.current_program}</span></div>}
              {d.alerts?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {d.alerts.map((a) => (
                    <span key={a} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200" data-testid="dog-alert">{a}</span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {dogs.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No dogs yet.</p>}
      </div>

      {/* Create / edit modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Dog" : "New Dog"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Dog name"><Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="dog-field-name" /></Field>
            <Field label="Breed"><Input value={form.breed} onChange={(e) => setF("breed", e.target.value)} data-testid="dog-field-breed" /></Field>
            <Field label="Owner">
              <Picker value={form.client_id} onChange={(v) => setF("client_id", v)} options={ref.clients.map((c) => ({ value: c.id, label: c.full_name }))} placeholder="Select owner" testid="dog-field-client" />
            </Field>
            <Field label="Location">
              <Picker value={form.location_id} onChange={(v) => setF("location_id", v)} options={ref.locations.map((l) => ({ value: l.id, label: l.name }))} placeholder="Select location" testid="dog-field-location" />
            </Field>
            <Field label="Assigned trainer">
              <Picker value={form.assigned_trainer_id} onChange={(v) => setF("assigned_trainer_id", v)} options={ref.trainers.map((t) => ({ value: t.id, label: t.full_name }))} placeholder="Select trainer" testid="dog-field-trainer" />
            </Field>
            <Field label="Current level">
              <Picker value={form.current_level} onChange={(v) => setF("current_level", v)} options={LEVELS} testid="dog-field-level" />
            </Field>
            <Field label="Age"><Input value={form.age} onChange={(e) => setF("age", e.target.value)} placeholder="e.g. 1.5 years" data-testid="dog-field-age" /></Field>
            <Field label="Gender">
              <Picker value={form.gender} onChange={(v) => setF("gender", v)} options={[{ value: "Male", label: "Male" }, { value: "Female", label: "Female" }]} placeholder="Select" testid="dog-field-gender" />
            </Field>
            <Field label="Weight"><Input value={form.weight} onChange={(e) => setF("weight", e.target.value)} placeholder="e.g. 24 kg" data-testid="dog-field-weight" /></Field>
            <Field label="Vaccination status">
              <Picker value={form.vaccination_status} onChange={(v) => setF("vaccination_status", v)} options={[{ value: "up_to_date", label: "Up to date" }, { value: "due_soon", label: "Due soon" }, { value: "overdue", label: "Overdue" }]} testid="dog-field-vaccination" />
            </Field>
            <Field label="Progress status">
              <Picker value={form.progress_status} onChange={(v) => setF("progress_status", v)} options={[{ value: "on_track", label: "On track" }, { value: "needs_attention", label: "Needs attention" }]} testid="dog-field-progress" />
            </Field>
            <Field label="Behaviour notes" full><Textarea value={form.behaviour_notes} onChange={(e) => setF("behaviour_notes", e.target.value)} data-testid="dog-field-behaviour" /></Field>
            <Field label="Training goals" full><Textarea value={form.training_goals} onChange={(e) => setF("training_goals", e.target.value)} data-testid="dog-field-goals" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="dog-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading text-2xl">{detail.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="flex items-center gap-2">
                  <StatusBadge value={detail.current_level} />
                  <StatusBadge value={detail.progress_status} />
                  <StatusBadge value={detail.vaccination_status} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Info icon={DogIcon} label="Breed" value={detail.breed} />
                  <Info icon={Cake} label="Age" value={detail.age} />
                  <Info icon={Weight} label="Weight" value={detail.weight} />
                  <Info icon={HeartPulse} label="Gender" value={detail.gender} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Owner</div>
                  {detail.owner ? (
                    <div className="p-3 rounded-lg border border-border/60 text-sm">
                      <div className="font-medium">{detail.owner.full_name}</div>
                      {detail.owner.email && <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-1"><Mail size={12} /> {detail.owner.email}</div>}
                      {detail.owner.phone && <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-0.5"><Phone size={12} /> {detail.owner.phone}</div>}
                      {detail.owner.address && <div className="text-muted-foreground text-xs mt-0.5">{detail.owner.address}</div>}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No owner linked.</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Block title="Location" value={detail.location_name} />
                  <Block title="Assigned Trainer" value={detail.trainer_name} />
                </div>
                <Block title="Behaviour Notes" value={detail.behaviour_notes} />
                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1 flex items-center gap-1.5"><Target size={13} /> Training Goals</div>
                  <p className="text-sm">{detail.training_goals || "—"}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Block title="Current Program" value={detail.current_program} />
                  <div>
                    <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1">Recommended Next Step</div>
                    <p className="text-sm text-primary font-medium">{detail.recommended_next_step || "—"}</p>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><CalendarDays size={13} /> Enrolled Classes</div>
                  <div className="space-y-2">
                    {detail.enrolled_classes?.length ? detail.enrolled_classes.map((c) => (
                      <div key={c.class_id} className="p-3 rounded-lg border border-border/60" data-testid="dog-enrolled-class">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{c.class_name}</span>
                          <StatusBadge value={c.status} />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-muted-foreground">{fmtDate(c.date)} · {c.time}</span>
                          <StatusBadge value={c.attendance_status} />
                        </div>
                      </div>
                    )) : <p className="text-sm text-muted-foreground">Not enrolled in any classes.</p>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><HeartPulse size={13} /> Attendance History</div>
                  <div className="space-y-1.5">
                    {detail.attendance_history?.length ? detail.attendance_history.map((c) => (
                      <div key={c.class_id} className="flex items-center justify-between text-sm p-2 rounded-md bg-muted/40" data-testid="dog-attendance-item">
                        <span>{c.class_name} · {fmtDate(c.date)}</span>
                        <StatusBadge value={c.attendance_status} />
                      </div>
                    )) : <p className="text-sm text-muted-foreground">No attendance recorded yet.</p>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><ClipboardList size={13} /> Homework</div>
                  <div className="space-y-2">
                    {detail.homework?.length ? detail.homework.map((h) => (
                      <div key={h.id} className="p-3 rounded-lg border border-border/60">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{h.title}</span>
                          <StatusBadge value={h.status} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{h.instructions}</p>
                        <p className="text-xs text-muted-foreground mt-1">Due {fmtDate(h.due_date)}</p>
                      </div>
                    )) : <p className="text-sm text-muted-foreground">No homework assigned.</p>}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5"><StickyNote size={13} /> Trainer Notes</div>
                  <div className="p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                    Private trainer notes — coming soon.
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" className="flex-1 gap-2" onClick={() => { setDetail(null); openEdit(detail); }} data-testid="dog-detail-edit"><Pencil size={15} /> Edit</Button>
                    <Button variant="outline" className="flex-1 gap-2 text-destructive" onClick={() => remove(detail)} data-testid="dog-detail-delete"><Trash2 size={15} /> Delete</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={`grid gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-sm">{label}</Label>
    {children}
  </div>
);

function Picker({ value, onChange, options, placeholder = "Select", testid }) {
  return (
    <Select value={value ? String(value) : ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="p-3 rounded-lg border border-border/60">
    <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon size={13} /> {label}</div>
    <div className="text-sm font-medium mt-1">{value || "—"}</div>
  </div>
);

const Block = ({ title, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1">{title}</div>
    <p className="text-sm">{value || "—"}</p>
  </div>
);
